import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsMessageService, PromptService } from '@services';
import { RmsModule } from '@rms';
import { take } from 'rxjs/operators';
import { RmsPulldownRef } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import {
  PaymentsDataService, PaymentOptions, BraintreeMatchRow, BraintreePostResult
} from '../../../services/payments-data.service';

// =====================================================================
// BatchProcessComponent — "Batch Process" (7.2.30) · Braintree
// ---------------------------------------------------------------------
// 1. Pick a date range → Pull from Braintree (coffee merchant account only)
// 2. Each transaction is matched to RMS (receipt by Braintree ID, else order + amount)
//    and gets a Result: READY / CREATE / POSTED / … (see match_braintree_transactions)
// 3. Tick rows → Post selected. The API re-reads every ticked ID from Braintree and
//    SQL re-matches before posting — the preview is never trusted.
//    READY  → post the matched receipt (deposit date = Braintree disbursement date)
//    CREATE → new receipt for the order + post it
//    LINK   → already posted; just save the Braintree ID on it
// Closes with true when anything was posted (the caller refreshes).
// =====================================================================

interface BatchData { Options: PaymentOptions | null; }

interface ResultChip { key: string; label: string; css: string; }

type Row = BraintreeMatchRow & { Selected: boolean; Outcome?: BraintreePostResult | null };

@Component({
  selector: 'app-batch-process',
  imports: [RmsModule],
  templateUrl: './batch-process.component.html',
  styleUrl: './batch-process.component.scss'
})
export class BatchProcessComponent implements OnInit {
  From: string;
  To: string = PaymentsDataService.today();
  BankID: number | null = null;
  PaymentDetailTypeID: number | null = null;

  Banks: { BankID: number; BankName: string }[] = [];
  DetailTypes: { PaymentDetailTypeID: number; DetailTypeName: string }[] = [];

  Rows: Row[] = [];
  Environment = '';
  Total = 0;
  Truncated = false;
  Pulled = false;
  Loading = false;
  Posting = false;
  Changed = false;
  Error = '';
  Filter = 'ALL';

  // a row from the last post, kept by Braintree ID so it survives the refresh
  private outcomes = new Map<string, BraintreePostResult>();

  readonly Chips: ResultChip[] = [
    { key: 'READY', label: 'Ready to post', css: 'r-ready' },
    { key: 'CREATE', label: 'New receipt', css: 'r-create' },
    { key: 'POSTED', label: 'Already posted', css: 'r-posted' },
    { key: 'AMOUNT_DIFF', label: 'Amount differs', css: 'r-warn' },
    { key: 'ORDER_PAID', label: 'Order already paid', css: 'r-warn' },
    { key: 'EXCEPTION', label: 'Has exception', css: 'r-warn' },
    { key: 'NOT_SETTLED', label: 'Not settled', css: 'r-muted' },
    { key: 'REFUND', label: 'Refund', css: 'r-warn' },
    { key: 'FAILED', label: 'Failed', css: 'r-muted' },
    { key: 'NO_MATCH', label: 'No match', css: 'r-bad' },
  ];

  constructor(
    @Inject(RMS_PULLDOWN_DATA) public data: BatchData,
    public ref: RmsPulldownRef,
    private paymentsData: PaymentsDataService,
    private prompt: PromptService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    this.From = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const o = data.Options;
    this.Banks = (o?.Banks ?? []).filter(b => b.IsActive);
    this.DetailTypes = (o?.DetailTypes ?? []).filter(t => t.IsActive && !t.IsWithdrawal && t.PostsToLedger);
    this.BankID = this.Banks[0]?.BankID ?? null;
    this.PaymentDetailTypeID =
      this.DetailTypes.find(t => /^credit card$/i.test(t.DetailTypeName))?.PaymentDetailTypeID
      ?? this.DetailTypes.find(t => /credit card/i.test(t.DetailTypeName))?.PaymentDetailTypeID
      ?? null;
  }

  ngOnInit() { }

  // ---------------------------------------------------------------------
  // View helpers
  // ---------------------------------------------------------------------

  count(key: string): number {
    return this.Rows.filter(r => r.Result === key).length;
  }

  get VisibleChips(): ResultChip[] {
    return this.Chips.filter(c => this.count(c.key) > 0);
  }

  get ShownRows(): Row[] {
    return this.Filter === 'ALL' ? this.Rows : this.Rows.filter(r => r.Result === this.Filter);
  }

  get Selected(): Row[] {
    return this.Rows.filter(r => r.Selected && r.Action);
  }

  get SelectedTotal(): number {
    return Math.round(this.Selected.reduce((s, r) => s + (Number(r.Amount) || 0), 0) * 100) / 100;
  }

  get AllShownSelected(): boolean {
    const can = this.ShownRows.filter(r => r.Action);
    return can.length > 0 && can.every(r => r.Selected);
  }

  toggleAllShown(checked: boolean) {
    this.ShownRows.filter(r => r.Action).forEach(r => r.Selected = checked);
  }

  chipCss(result: string): string {
    return this.Chips.find(c => c.key === result)?.css ?? 'r-muted';
  }

  chipLabel(result: string): string {
    return this.Chips.find(c => c.key === result)?.label ?? result;
  }

  actionLabel(action: string | null): string {
    switch (action) {
      case 'POST': return 'Post receipt';
      case 'CREATE': return 'Create + post';
      case 'LINK': return 'Save Braintree ID';
      default: return '';
    }
  }

  setFilter(key: string) {
    this.Filter = this.Filter === key ? 'ALL' : key;
  }

  // ---------------------------------------------------------------------
  // Pull
  // ---------------------------------------------------------------------

  async pull() {
    if (this.Loading || this.Posting) return;
    if (!this.From || !this.To) { this.Error = 'Choose a From and To date.'; return; }
    if (this.From > this.To) { this.Error = 'From must be before To.'; return; }

    this.Loading = true;
    this.Error = '';
    try {
      const r = await lastValueFrom(this.paymentsData.getBraintreeTransactions(this.From, this.To));
      this.Environment = r?.Environment ?? '';
      this.Total = r?.Total ?? 0;
      this.Truncated = !!r?.Truncated;
      // tick everything that has something to do; keep the last post's outcome on its row
      this.Rows = (r?.Rows ?? []).map(x => ({
        ...x,
        Selected: !!x.Action,
        Outcome: this.outcomes.get(x.TransactionId) ?? null
      }));
      this.Pulled = true;
      if (this.Filter !== 'ALL' && !this.count(this.Filter)) this.Filter = 'ALL';
    } catch (err: any) {
      this.Error = err?.error?.error ?? 'Could not read Braintree.';
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------
  // Post
  // ---------------------------------------------------------------------

  confirmPost() {
    const rows = this.Selected;
    if (!rows.length || this.Posting) return;
    if (!this.BankID) { this.Error = 'Choose the bank.'; return; }
    if (!this.PaymentDetailTypeID && rows.some(r => r.Action === 'CREATE')) {
      this.Error = 'Choose the payment type for new receipts.';
      return;
    }

    const creates = rows.filter(r => r.Action === 'CREATE').length;
    const posts = rows.filter(r => r.Action === 'POST').length;
    const links = rows.filter(r => r.Action === 'LINK').length;
    const parts = [
      posts ? `post ${posts} receipt${posts === 1 ? '' : 's'}` : '',
      creates ? `create and post ${creates} new receipt${creates === 1 ? '' : 's'}` : '',
      links ? `save the Braintree ID on ${links} posted receipt${links === 1 ? '' : 's'}` : ''
    ].filter(Boolean).join(', ');

    const { closed$ } = this.prompt.open({
      promptText: `This will ${parts} (${this.SelectedTotal.toFixed(2)} in total). Posted receipts go to the ledger and cannot be edited. Continue?`,
      buttons: [{ label: 'Post', buttonClass: 'btn-bg-green', value: true }]
    });
    closed$.pipe(take(1)).subscribe(async (ok: any) => { if (ok) await this.post(); });
  }

  private async post() {
    const ids = this.Selected.map(r => r.TransactionId);
    this.Posting = true;
    this.Error = '';
    try {
      const results = await lastValueFrom(this.paymentsData.postBraintreeBatch(ids, this.BankID!, this.PaymentDetailTypeID!));
      (results ?? []).forEach(x => this.outcomes.set(x.TransactionId, x));
      const done = (results ?? []).filter(x => x.Outcome === 'DONE').length;
      const errors = (results ?? []).filter(x => x.Outcome === 'ERROR').length;
      const skipped = (results ?? []).filter(x => x.Outcome === 'SKIPPED').length;
      if (done) this.Changed = true;

      const text = `${done} done` + (errors ? `, ${errors} with errors` : '') + (skipped ? `, ${skipped} skipped` : '') + '.';
      if (errors) this.msg.warn(`Batch finished: ${text} See the Outcome column.`);
      else this.msg.success(`Batch finished: ${text}`);

      await this.pull();   // refresh results (posted rows now show as Already posted)
    } catch (err: any) {
      this.Error = err?.error?.error ?? 'The batch could not be posted.';
    } finally {
      this.Posting = false;
      this.cdr.markForCheck();
    }
  }

  close() {
    this.ref.close(this.Changed);
  }
}
